import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MensagemService, ProgressoService } from 'maestro-ng';
import { SelectItem } from 'primeng/api';
import { FonteDados } from '../fonte-dados.model';
import { FonteDadosService } from '../fonte-dados.service';
import { FonteDadosProxyRotaAbstract } from './fonte-dados-proxy-rota-abstract.model';
import { FonteDadosProxyRotaPadrao } from './fonte-dados-proxy-rota-padrao.model';
import { FonteDadosProxyRotaPessoaMetadados } from './fonte-dados-proxy-rota-pessoa-metadados.model';
import { FonteDadosProxyParametro, FonteDadosProxyParametroTipo } from './fonte-dados-proxy-parametro';
import { HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FonteDadosProxyRotaProcessoMetadados } from './fonte-dados-proxy-rota-processo-metadados.model';
import { FonteDadosMensagemErroService } from '../fonte-dados-mensagem-erro.service';

@Component({
  selector: 'app-fonte-dados-proxy',
  templateUrl: './fonte-dados-proxy.component.html',
  styleUrls: ['./fonte-dados-proxy.component.css'],
})
export class FonteDadosProxyComponent implements OnInit, OnDestroy {
  protected readonly parametroProxyTipo = FonteDadosProxyParametroTipo;
  public rotasItens: SelectItem<FonteDadosProxyRotaAbstract>[];
  public fonteDadosProxy: FonteDadosProxyRotaAbstract;

  public resultado: { tipo: string; conteudo: any; url?: SafeResourceUrl };

  @Input()
  public fonteDados: FonteDados;

  public paramSessao: Map<string, any> = new Map<string, any>();

  constructor(
    private service: FonteDadosService,
    private mensagemService: MensagemService,
    private progressoService: ProgressoService,
    private sanitizer: DomSanitizer,
    private fonteDadosMensagemErroService: FonteDadosMensagemErroService
  ) {}

  ngOnInit(): void {
    this.rotasItens = this.criarSelectItemsRotas();
  }

  public buscar() {
    if (this.fonteDadosProxy) {
      this.limparResultado();
      if (this.validarCamposObrigatorios()) {
        this.progressoService.modal();
        this.service
          .fonteDadosExtrator(this.fonteDados.nome, this.fonteDadosProxy)
          .then((response) => {
            this.processarHttpResponse(response);
          })
          .catch((resposta) => {
            this.fonteDadosMensagemErroService.apresentarErro(
              'Erro',
              'Dados do Proxy',
              resposta,
              'Erro ao recuperar os dados do proxy. : ' + this.fonteDadosProxy.rota + '  ' + resposta.message
            );
          })
          .finally(() => this.progressoService.fechar());
      }
    } else {
      this.mensagemService.addAlerta('Atenção', 'É obrigatorio informar uma rota para a pesquisa');
    }
  }

  private processarHttpResponse(response: HttpResponse<any>) {
    if (response.ok) {
      this.resultado = {
        tipo: this.fonteDadosProxy.tipoResposta,
        conteudo: response.body,
      };
      if (this.fonteDadosProxy.tipoResposta === 'blob') {
        this.resultado.url = this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(this.resultado.conteudo));
      }
    }
  }

  private validarCamposObrigatorios(): boolean {
    const invalidos = this.fonteDadosProxy.parametros.filter((pp) => pp.obrigatorio && !pp.valor);
    invalidos.forEach((pp) => {
      this.mensagemService.addAlerta('Atenção', `O campo ${pp.rotulo} é obrigatório para a pesquisa`);
    });
    return invalidos.length === 0;
  }

  public limpar() {
    this.limparResultado();
    this.fonteDadosProxy = null;
    this.paramSessao = new Map<string, any>();
    this.rotasItens.forEach((rota) => rota.value.limparValores());
  }

  public salvarParametrosSessao(nome: string, valor: string) {
    this.paramSessao.set(nome, valor);
  }

  public selecionarRota(valor: FonteDadosProxyRotaAbstract) {
    valor.parametrosPath.forEach((param) => {
      const key = 'path:' + param.nome;
      this.atualizaComValorSessao(key, param);
    });
    valor.parametrosQuery.forEach((param) => {
      const key = 'query:' + param.nome;
      this.atualizaComValorSessao(key, param);
    });
  }

  private atualizaComValorSessao(key: string, param: FonteDadosProxyParametro) {
    if (this.paramSessao.has(key)) {
      param.valor = this.paramSessao.get(key);
    }
  }

  public criarSelectItemsRotas(): SelectItem[] {
    return [
      { label: 'Processo Metadados por Id', value: new FonteDadosProxyRotaProcessoMetadados() },
      {
        label: 'Processo Metadados por Número',
        value: new FonteDadosProxyRotaPadrao('recuperarProcessoMetadadosPorNumero', {
          pathParam: {
            nome: 'numeroProcesso',
            rotulo: 'Número do Processo',
            obrigatorio: true,
            titulo: 'Número do processo na fonte de dados',
          },
        }),
      },
      {
        label: 'Processo Documento Metadados',
        value: new FonteDadosProxyRotaPadrao('recuperarProcessoDocumentosMetadadosPorProcessoId'),
      },
      {
        label: 'Processo Documento Conteúdo por DocumentoID',
        value: new FonteDadosProxyRotaPadrao('recuperarProcessoDocumentoConteudoPorDocumentoId', {
          tipoResposta: 'blob',
          pathParam: {
            nome: 'documentoId',
            rotulo: 'Documento ID',
            obrigatorio: true,
            titulo: 'Identificador do documento na fonte de dados',
          },
        }),
      },
      {
        label: 'Processo Movimento Metadados',
        value: new FonteDadosProxyRotaPadrao('recuperarProcessoMovimentosMetadadosPorProcessoId'),
      },
      {
        label: 'Processo Partes Metadados',
        value: new FonteDadosProxyRotaPadrao('recuperarProcessoPartesMetadadosPorProcessoId'),
      },
      { label: 'Pessoas Metadados por Id', value: new FonteDadosProxyRotaPessoaMetadados() },
    ];
  }

  ngOnDestroy(): void {
    this.limparResultado();
  }

  private limparResultado() {
    if (this.resultado && this.resultado.tipo === 'blob') {
      window.URL.revokeObjectURL(this.resultado.url?.toString() || '');
    }
    this.resultado = null;
  }
}
